import { PAGINATION_PAGE_PARAM, PAGINATION_PER_PAGE_PARAM, SearchParamController } from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import NotificationsDelegate from './notifications.delegate';
import { NotificationsConstants } from './notifications.enums';
import {
    buildNotificationsQuery,
    parseNotificationsPeriod,
    parseTypeTab,
    parseUnreadParam,
    serializeNotificationsPeriod,
} from './notifications.utils';
import { resolveNotificationDestination } from '../../utils/notifications';
import { decrementUnread } from '../../data/notifications.store';

/**
 * Admin Notifications screen — paginated, aba-scoped feed of failed
 * advances / settlements, trouble tickets and support-ticket status changes
 * (GET /api/admin/notifications/:aba). Every filter and page/perPage lives
 * in the URL so a refresh restores the view.
 */
class NotificationsController extends Inherit(ClockoutController, NotificationsDelegate, SearchParamController) {
    state = {
        loading: true,
        loadError: false,
        items: [],
        pagination: null,
        listPageSize: NotificationsConstants.DEFAULT_PAGE_SIZE,
        typeTab: NotificationsConstants.LIST_TYPE_DEFAULT,
        unreadOnly: false,
        timeFrame: NotificationsConstants.TIMEFRAME_DEFAULT,
        dateRange: null,
    };

    async initialize() {
        if (!this.user?.access?.length) {
            this.navigateLogin();
            return;
        }

        const { initialPage, initialPerPage } = this.readPaginationFromUrl();
        const typeTab = parseTypeTab(this.getSearchParamValue(NotificationsConstants.LIST_TYPE_PARAM));
        const unreadOnly = parseUnreadParam(this.getSearchParamValue(NotificationsConstants.LIST_UNREAD_PARAM));
        const { timeFrame, dateRange } = parseNotificationsPeriod(
            this.getSearchParamValue(NotificationsConstants.TIMEFRAME_PARAM),
            this.getSearchParamValue(NotificationsConstants.DATE_RANGE_PARAM),
        );

        this.setState({ listPageSize: initialPerPage, typeTab, unreadOnly, timeFrame, dateRange }, () => {
            this.loadNotificationsPage(initialPage);
        });
    }

    async cleanup() {
        // Invalidate in-flight loads so a late response can't setState on an unmounted screen.
        this._loadToken = (this._loadToken || 0) + 1;
    }

    readPaginationFromUrl() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
            initialPerPage:
                Number.isFinite(rawPerPage) && rawPerPage > 0 ? rawPerPage : NotificationsConstants.DEFAULT_PAGE_SIZE,
        };
    }

    async loadNotificationsPage(page = 1) {
        const token = (this._loadToken = (this._loadToken || 0) + 1);
        this.setLoading(true);
        this.setLoadError(false);

        const { listPageSize, typeTab, unreadOnly, timeFrame, dateRange } = this.state;
        const query = buildNotificationsQuery({
            page,
            perPage: listPageSize,
            typeTab,
            unreadOnly,
            timeFrame,
            dateRange,
        });

        try {
            const data = await this.data.fetchNotifications(query);
            if (token !== this._loadToken) return;
            this.setPagination(data?.items, data?.pageinfo);
        } catch (error) {
            if (token !== this._loadToken) return;
            console.error('Error loading notifications:', error);
            // Empty page (not stale rows) + inline error copy; the toast is the loud part.
            this.setPagination([], { page, perPage: listPageSize, count: 0 });
            this.setLoadError(true);
            this.props.notificationApi?.error({
                message: 'Could not load notifications',
                description: error?.response?.data?.error || error?.message || 'Please try again.',
            });
        } finally {
            if (token === this._loadToken) this.setLoading(false);
        }
    }

    /** Writes `period` / `range` to the URL (range only for a complete custom range). */
    persistPeriod(timeFrame, dateRange) {
        const { period, range } = serializeNotificationsPeriod(timeFrame, dateRange);
        this.setSearchParamValue(NotificationsConstants.TIMEFRAME_PARAM, period);
        this.setSearchParamValue(NotificationsConstants.DATE_RANGE_PARAM, range);
    }

    handleTableChange(pagination) {
        const nextSize = pagination?.pageSize ?? this.state.listPageSize;
        if (nextSize !== this.state.listPageSize) {
            this.setPerPage(nextSize, () => this.loadNotificationsPage(1));
            return;
        }
        this.loadNotificationsPage(pagination?.current || 1);
    }

    /** Tabs persist `type` themselves (searchParamKey); we just reset the page. */
    handleTypeTabChange(key) {
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setTypeTab(parseTypeTab(key), () => this.loadNotificationsPage(1));
    }

    handleUnreadOnlyChange(checked) {
        this.setSearchParamValue(NotificationsConstants.LIST_UNREAD_PARAM, checked ? 'true' : null);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setUnreadOnly(checked, () => this.loadNotificationsPage(1));
    }

    handleTimeFrameChange(timeFrame) {
        const nextRange = timeFrame === 'custom' ? this.state.dateRange : null;
        this.persistPeriod(timeFrame, nextRange);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setPeriod(timeFrame, nextRange, () => {
            // A custom frame needs both ends before it can be applied.
            if (timeFrame === 'custom' && (!nextRange?.[0] || !nextRange?.[1])) return;
            this.loadNotificationsPage(1);
        });
    }

    handleDateRangeChange(dates) {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            this.persistPeriod('custom', null);
            this.setPeriod('custom', null);
            return;
        }
        this.persistPeriod('custom', dates);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setPeriod('custom', dates, () => this.loadNotificationsPage(1));
    }

    handleRefresh() {
        this.loadNotificationsPage(this.state.pagination?.current || 1);
    }

    /**
     * Row click: optimistically mark read (badge + row), persist in the
     * background, then deep-link by resourceType. A failed mark-read is
     * non-blocking — the header badge resyncs on its next poll.
     */
    async handleRowClick(record) {
        if (!record?.id) return;
        if (!record.readAt) {
            this.markItemRead(record.id);
            decrementUnread(1);
            this.data.markNotificationsRead([record.id]).catch(() => {
                /* non-blocking */
            });
        }
        const destination = resolveNotificationDestination(record);
        if (destination?.kind === 'ticket') this.navigateTicket(destination.id);
        else if (destination?.kind === 'user') this.navigateUser(destination.id);
    }
}

export default NotificationsController;
